import {
  Applicant,
  ApplicantDoc,
  ApplicantDocument,
  ApplicantDocumentKind,
  ApplicantDocumentSource,
} from "../types/applicants";
import { Candidate, CandidateDoc, Candidate_FB } from "../types/recruiting";
import { AppUser } from "../types/users";
import db, { BASE_FUNCTIONS_URL } from "../types/firebase";
import { storeApplicant } from "./applicants-api";
import { getCandidateFromFB } from "../utils/candidate-utils";
import { collection, getDocs, query, where } from "firebase/firestore";
 
export const APPLICANT_DOCUMENT_MAX_FILE_SIZE_BYTES = 3 * 1024 * 1024;
export const APPLICANT_DOCUMENT_ALLOWED_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
] as const;

type ApplicantDocumentAllowedContentType =
  (typeof APPLICANT_DOCUMENT_ALLOWED_CONTENT_TYPES)[number];

export interface DocumentAccessContext {
  authToken?: string | null;
  questionnaireSessionToken?: string | null;
}

export interface QuestionnaireDocumentSession {
  sessionToken: string;
  expiresAt: string;
  applicantId: string;
}

export class ApplicantDocumentApiError extends Error {
  code?: string;
  status: number;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApplicantDocumentApiError";
    this.status = status;
    this.code = code;
  }
}

const CANDIDATE_DOCUMENT_MAPPINGS: Array<{
  field: keyof Candidate;
  kind: ApplicantDocumentKind;
}> = [
  { field: "kyrgyzPhotoImageUrl", kind: "candidate_photo" },
  { field: "kyrgyzPassportImageUrl", kind: "passport_image" },
  { field: "passportImageUrl", kind: "passport_image" },
  { field: "imageUrl", kind: "passport_image" },
  { field: "kyrgyzPaymentReceiptImageUrl", kind: "payment_receipt" },
];

const DOCUMENT_SOURCE_PRIORITY: Record<ApplicantDocumentSource, number> = {
  questionnaire: 2,
  recruiting: 1,
};

const IMAGE_CONTENT_TYPE_BY_EXTENSION: Record<string, ApplicantDocumentAllowedContentType> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
};

const safeDateValue = (dateValue?: string) => {
  if (!dateValue) {
    return 0;
  }

  const parsed = new Date(dateValue).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getExtensionFromFileName = (fileName?: string) => {
  if (!fileName || !fileName.includes(".")) {
    return "";
  }

  return fileName.split(".").pop()?.toLowerCase() || "";
};

const getContentTypeFromExtension = (extension?: string) => {
  if (!extension) {
    return undefined;
  }

  return IMAGE_CONTENT_TYPE_BY_EXTENSION[extension.toLowerCase()];
};

const buildDocumentFingerprint = (document: ApplicantDocument) => {
  return `${document.kind}::${document.storagePath || document.url || document.fileName || ""}`;
};

const normalizeDocuments = (documents: ApplicantDocument[]) => {
  const deduped = new Map<string, ApplicantDocument>();

  documents.forEach((document) => {
    if (!document.url && !document.storagePath) {
      return;
    }

    const normalizedDocument: ApplicantDocument = {
      ...document,
      extension:
        document.extension ||
        getExtensionFromFileName(document.fileName) ||
        getExtensionFromFileName(document.storagePath),
      contentType:
        document.contentType ||
        getContentTypeFromExtension(
          document.extension ||
            getExtensionFromFileName(document.fileName) ||
            getExtensionFromFileName(document.storagePath)
        ),
    };

    const key = buildDocumentFingerprint(normalizedDocument);
    const existing = deduped.get(key);

    if (existing) {
      deduped.set(key, {
        ...existing,
        ...normalizedDocument,
      });
      return;
    }

    deduped.set(key, normalizedDocument);
  });

  return Array.from(deduped.values()).sort((first, second) => {
    const dateDifference = safeDateValue(second.uploadedAt) - safeDateValue(first.uploadedAt);
    if (dateDifference !== 0) {
      return dateDifference;
    }

    const sourceDifference =
      DOCUMENT_SOURCE_PRIORITY[second.source] - DOCUMENT_SOURCE_PRIORITY[first.source];
    if (sourceDifference !== 0) {
      return sourceDifference;
    }

    const firstKey = first.storagePath || first.url || first.fileName || "";
    const secondKey = second.storagePath || second.url || second.fileName || "";
    return firstKey.localeCompare(secondKey);
  });
};

const parseStoragePathFromUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url);
    const [, encodedPath = ""] = parsedUrl.pathname.split("/o/");
    return decodeURIComponent(encodedPath);
  } catch (error) {
    console.error("Failed to parse Firebase Storage URL:", error);
    return "";
  }
};

const createDocumentFromCandidateField = (
  candidateId: string,
  candidate: Candidate,
  kind: ApplicantDocumentKind,
  url: string
): ApplicantDocument => {
  const storagePath = parseStoragePathFromUrl(url);
  const fileName = storagePath.split("/").pop() || "";
  const extension = getExtensionFromFileName(fileName);

  return {
    kind,
    source: "recruiting",
    url,
    storagePath,
    fileName,
    extension,
    contentType: getContentTypeFromExtension(extension),
    uploadedAt: candidate.updatedAt || candidate.createdAt || "",
    candidateId,
  };
};

const loadCandidatesByApplicantId = async (applicantId: string) => {
  if (!applicantId) {
    return [];
  }

  const snapshot = await getDocs(
    query(collection(db, "candidates"), where("applicantId", "==", applicantId))
  );

  return snapshot.docs.map((candidateSnapshot) => ({
    id: candidateSnapshot.id,
    candidate: getCandidateFromFB(candidateSnapshot.data() as Candidate_FB),
  }));
};

const loadCandidatesByEmail = async (email: string) => {
  if (!email) {
    return [];
  }

  const snapshot = await getDocs(query(collection(db, "candidates"), where("email", "==", email)));

  return snapshot.docs.map((candidateSnapshot) => ({
    id: candidateSnapshot.id,
    candidate: getCandidateFromFB(candidateSnapshot.data() as Candidate_FB),
  }));
};

const getAllowedContentType = (file: File) => {
  if (
    file.type &&
    APPLICANT_DOCUMENT_ALLOWED_CONTENT_TYPES.includes(
      file.type as ApplicantDocumentAllowedContentType
    )
  ) {
    return file.type as ApplicantDocumentAllowedContentType;
  }

  const extension = getExtensionFromFileName(file.name);
  return getContentTypeFromExtension(extension);
};

const fileToDataUrl = (file: File) => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") {
        resolve(result);
        return;
      }

      reject(new ApplicantDocumentApiError("Invalid file payload.", 400, "invalid_request"));
    };

    reader.onerror = () => {
      reject(
        new ApplicantDocumentApiError(
          "The selected file could not be read.",
          400,
          "invalid_request"
        )
      );
    };

    reader.readAsDataURL(file);
  });
};

const buildDocumentRequestHeaders = (accessContext: DocumentAccessContext) => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (accessContext.authToken) {
    headers.Authorization = `Bearer ${accessContext.authToken}`;
  } else if (accessContext.questionnaireSessionToken) {
    headers["X-Questionnaire-Session"] = accessContext.questionnaireSessionToken;
  }

  return headers;
};

const parseDocumentApiResponse = async (response: Response) => {
  const payload = await response.json();

  if (!response.ok) {
    throw new ApplicantDocumentApiError(
      payload?.data?.message || "The document request failed.",
      response.status,
      payload?.data?.code
    );
  }

  return payload?.data || {};
};

const postDocumentApi = async (
  path: string,
  body: Record<string, unknown>,
  accessContext: DocumentAccessContext = {}
) => {
  const response = await fetch(BASE_FUNCTIONS_URL + path, {
    method: "POST",
    headers: buildDocumentRequestHeaders(accessContext),
    body: JSON.stringify(body),
  });

  return parseDocumentApiResponse(response);
};

const postDocumentApiForBlob = async (
  path: string,
  body: Record<string, unknown>,
  accessContext: DocumentAccessContext = {}
) => {
  const response = await fetch(BASE_FUNCTIONS_URL + path, {
    method: "POST",
    headers: buildDocumentRequestHeaders(accessContext),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    await parseDocumentApiResponse(response);
  }

  return response.blob();
};

export const createQuestionnaireDocumentSession = async (
  questionnaireId: string,
  email: string
) => {
  const data = await postDocumentApi("/v1/questionnaire/document-session", {
    questionnaireId,
    email,
  });

  return {
    sessionToken: data.sessionToken as string,
    expiresAt: data.expiresAt as string,
    applicantId: data.applicantId as string,
  } as QuestionnaireDocumentSession;
};

export const isImageFile = (file: File) => {
  if (!file) {
    return false;
  }

  const contentType = getAllowedContentType(file);
  if (!contentType) {
    return false;
  }

  return file.size <= APPLICANT_DOCUMENT_MAX_FILE_SIZE_BYTES;
};

export const getApplicantDocuments = (applicant?: Applicant) => {
  return normalizeDocuments(applicant?.uploadedDocuments || []);
};

export const mergeApplicantDocuments = (
  applicant: Applicant,
  incomingDocuments: ApplicantDocument[]
) => {
  return normalizeDocuments([...getApplicantDocuments(applicant), ...incomingDocuments]);
};

export const getApplicantDocumentsByKind = (
  applicant: Applicant | undefined,
  kind: ApplicantDocumentKind
) => {
  return getApplicantDocuments(applicant).filter((document) => document.kind === kind);
};

export const getPreferredApplicantDocument = (
  applicant: Applicant | undefined,
  kind: ApplicantDocumentKind
) => {
  return getApplicantDocumentsByKind(applicant, kind)[0];
};

export const hasApplicantDocument = (
  applicant: Applicant | undefined,
  kind: ApplicantDocumentKind
) => {
  return getApplicantDocumentsByKind(applicant, kind).length > 0;
};

export const normalizeCandidateDocuments = (candidateDoc: CandidateDoc): ApplicantDocument[] => {
  const documents: ApplicantDocument[] = [];

  CANDIDATE_DOCUMENT_MAPPINGS.forEach(({ field, kind }) => {
    const url = candidateDoc.candidate[field];
    if (typeof url !== "string" || !url) {
      return;
    }

    documents.push(
      createDocumentFromCandidateField(candidateDoc.id, candidateDoc.candidate, kind, url)
    );
  });

  return normalizeDocuments(documents);
};

export const syncApplicantDocumentsFromRecruiting = async (
  user: AppUser | null | undefined,
  applicantDoc: ApplicantDoc,
  persist: boolean = false
) => {
  if (!applicantDoc.id || !applicantDoc.applicant) {
    return { applicantDoc, changed: false, candidates: [] as CandidateDoc[] };
  }

  const candidatesById = await loadCandidatesByApplicantId(applicantDoc.id);
  const candidatesByEmail =
    candidatesById.length === 0 && applicantDoc.applicant.email
      ? await loadCandidatesByEmail(applicantDoc.applicant.email)
      : [];

  const candidates = [...candidatesById];
  candidatesByEmail.forEach((candidateDoc) => {
    if (candidates.some((existingCandidate) => existingCandidate.id === candidateDoc.id)) {
      return;
    }

    candidates.push(candidateDoc);
  });

  const recruitingDocuments = candidates.flatMap((candidateDoc) =>
    normalizeCandidateDocuments(candidateDoc)
  );

  if (recruitingDocuments.length === 0) {
    return { applicantDoc, changed: false, candidates };
  }

  const mergedDocuments = mergeApplicantDocuments(applicantDoc.applicant, recruitingDocuments);
  const currentDocuments = getApplicantDocuments(applicantDoc.applicant);
  const changed = JSON.stringify(currentDocuments) !== JSON.stringify(mergedDocuments);

  if (!changed) {
    return { applicantDoc, changed: false, candidates };
  }

  const updatedApplicantDoc: ApplicantDoc = {
    ...applicantDoc,
    applicant: {
      ...applicantDoc.applicant,
      uploadedDocuments: mergedDocuments,
    },
  };

  if (persist) {
    await storeApplicant(user, updatedApplicantDoc, false);
  }

  return { applicantDoc: updatedApplicantDoc, changed: true, candidates };
};

export const upsertQuestionnaireDocument = (
  applicant: Applicant,
  document: ApplicantDocument
) => {
  const filteredDocuments = getApplicantDocuments(applicant).filter((currentDocument) => {
    if (currentDocument.kind !== document.kind) {
      return true;
    }

    if (currentDocument.source !== document.source) {
      return true;
    }

    return document.source !== "questionnaire";
  });

  return normalizeDocuments([...filteredDocuments, document]);
};

export const uploadApplicantDocument = async (
  applicantDoc: ApplicantDoc,
  kind: ApplicantDocumentKind,
  file: File,
  accessContext: DocumentAccessContext
) => {
  if (!applicantDoc.id) {
    throw new Error("Applicant ID is required before uploading a document.");
  }

  if (!applicantDoc.applicant.sponsorId) {
    throw new Error("Sponsor ID is required before uploading a document.");
  }

  const contentType = getAllowedContentType(file);
  if (!contentType) {
    throw new ApplicantDocumentApiError(
      "Only JPEG and PNG images up to 3 MB are allowed.",
      400,
      "invalid_file_type"
    );
  }

  if (file.size > APPLICANT_DOCUMENT_MAX_FILE_SIZE_BYTES) {
    throw new ApplicantDocumentApiError(
      "Only JPEG and PNG images up to 3 MB are allowed.",
      413,
      "file_too_large"
    );
  }

  const fileData = await fileToDataUrl(file);
  const data = await postDocumentApi(
    "/v1/documents/applicant-upload",
    {
      applicantId: applicantDoc.id,
      kind,
      fileName: file.name || `${kind}.${contentType === "image/png" ? "png" : "jpg"}`,
      contentType,
      fileData,
    },
    accessContext
  );

  return data.document as ApplicantDocument;
};

export const getApplicantDocumentAccessUrl = async (
  applicantId: string,
  document: ApplicantDocument,
  accessContext: DocumentAccessContext
) => {
  if (document.url) {
    return document.url;
  }

  if (!document.storagePath) {
    return "";
  }

  const blob = await postDocumentApiForBlob(
    "/v1/documents/content",
    {
      applicantId,
      storagePath: document.storagePath,
    },
    accessContext
  );

  const objectUrl = URL.createObjectURL(blob);
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 5 * 60 * 1000);
  return objectUrl;
};

export const replaceApplicantQuestionnaireDocument = async (
  applicantDoc: ApplicantDoc,
  kind: ApplicantDocumentKind,
  file: File,
  accessContext: DocumentAccessContext
) => {
  const previousDocument = getApplicantDocuments(applicantDoc.applicant).find((document) => {
    return document.kind === kind && document.source === "questionnaire";
  });

  const contentType = getAllowedContentType(file);
  if (!contentType) {
    throw new ApplicantDocumentApiError(
      "Only JPEG and PNG images up to 3 MB are allowed.",
      400,
      "invalid_file_type"
    );
  }

  if (file.size > APPLICANT_DOCUMENT_MAX_FILE_SIZE_BYTES) {
    throw new ApplicantDocumentApiError(
      "Only JPEG and PNG images up to 3 MB are allowed.",
      413,
      "file_too_large"
    );
  }

  const fileData = await fileToDataUrl(file);
  const data = await postDocumentApi(
    "/v1/documents/applicant-upload",
    {
      applicantId: applicantDoc.id,
      kind,
      fileName: file.name || `${kind}.${contentType === "image/png" ? "png" : "jpg"}`,
      contentType,
      fileData,
      previousStoragePath: previousDocument?.storagePath || "",
    },
    accessContext
  );

  const uploadedDocument = data.document as ApplicantDocument;

  return {
    ...applicantDoc,
    applicant: {
      ...applicantDoc.applicant,
      uploadedDocuments: upsertQuestionnaireDocument(applicantDoc.applicant, uploadedDocument),
    },
  };
};

export const removeApplicantQuestionnaireDocument = async (
  applicantDoc: ApplicantDoc,
  document: ApplicantDocument,
  accessContext: DocumentAccessContext
) => {
  if (!applicantDoc.id) {
    throw new ApplicantDocumentApiError(
      "Applicant ID is required before deleting a document.",
      400,
      "invalid_request"
    );
  }

  if (document.source !== "questionnaire" || !document.storagePath) {
    throw new ApplicantDocumentApiError(
      "Only questionnaire-uploaded documents can be removed here.",
      400,
      "invalid_request"
    );
  }

  await postDocumentApi(
    "/v1/documents/delete",
    {
      applicantId: applicantDoc.id,
      storagePath: document.storagePath,
    },
    accessContext
  );

  return {
    ...applicantDoc,
    applicant: {
      ...applicantDoc.applicant,
      uploadedDocuments: getApplicantDocuments(applicantDoc.applicant).filter(
        (currentDocument) => currentDocument.storagePath !== document.storagePath
      ),
    },
  };
};

export const requiresUkWorkVisaImage = (applicant: Applicant | undefined) => {
  if (!applicant?.has_travelled_to_UK || !applicant.travel_details_UK) {
    return false;
  }

  const thresholdDate = new Date();
  thresholdDate.setFullYear(thresholdDate.getFullYear() - 2);
  thresholdDate.setHours(0, 0, 0, 0);

  return applicant.travel_details_UK.some((travel) => {
    if (travel.reason !== "Work") {
      return false;
    }

    const candidateDate = travel.to || travel.from;
    if (!candidateDate) {
      return false;
    }

    const parsedDate = new Date(candidateDate);
    if (Number.isNaN(parsedDate.getTime())) {
      return false;
    }

    return parsedDate >= thresholdDate;
  });
};
